import React, { useEffect, useState, useMemo } from "react";
import { Bar } from "react-chartjs-2";
import { Chart as ChartJS, BarElement, CategoryScale, LinearScale, Tooltip, Legend } from "chart.js";
import styled from "styled-components";
import { Info } from "lucide-react";
import { Tooltip as InfoTooltip } from "react-tooltip";

ChartJS.register(BarElement, CategoryScale, LinearScale, Tooltip, Legend);

const regionNameMapping = {
  MAZOWIECKIE: "mazowieckie",
  ŚLĄSKIE: "śląskie",
  PODKARPACKIE: "podkarpackie",
  LUBUSKIE: "lubuskie",
  ZACHODNIOPOMORSKIE: "zachodniopomorskie",
  WIELKOPOLSKIE: "wielkopolskie",
  DOLNOŚLĄSKIE: "dolnośląskie",
  ŁÓDZKIE: "łódzkie",
  ŚWIĘTOKRZYSKIE: "świętokrzyskie",
  POMORSKIE: "pomorskie",
  LUBELSKIE: "lubelskie",
  OPOLSKIE: "opolskie",
  PODLASKIE: "podlaskie",
  "WARMIŃSKO-MAZURSKIE": "warmińsko-mazurskie",
  "KUJAWSKO-POMORSKIE": "kujawsko-pomorskie",
  MAŁOPOLSKIE: "małopolskie",
};

const parseValue = (value) => {
  const num = parseFloat(value);
  return isNaN(num) ? 0 : num;
};

const formatNumber = (value) =>
  Number(value).toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, " ");

const monthsList = [
  "Styczeń", "Luty", "Marzec", "Kwiecień", "Maj", "Czerwiec",
  "Lipiec", "Sierpień", "Wrzesień", "Październik", "Listopad", "Grudzień"
];

// === Styled Components ===
const ChartTitle = styled.div`
  font-family: Arial, sans-serif;
  font-size: 12px;
  font-style: normal;
  font-weight: bold;
  color: #666;
  margin-bottom: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
`;

const InfoIconWrapper = styled.div`
  cursor: pointer;
  color: #666;
  transition: color 0.3s;
  &:hover { color: #000; }
  svg { width: 18px; height: 18px; }
`;

// Canonical colors
const GREEN = "#2e7d32";
const GREEN_BORDER = "#2e7d32";
const ORANGE = "#fb8c00";
const ORANGE_BORDER = "#fb8c00";

const TransactionTypesByRegion = ({ salesData = [], clientsData = [] }) => {
  const [availableYears, setAvailableYears] = useState([]);
  const [selectedYear, setSelectedYear] = useState("");
  const [selectedMonth, setSelectedMonth] = useState("00");

  useEffect(() => {
    if (salesData.length === 0) return;
    const years = [...new Set(salesData.map((row) => row.Rok.toString()))].sort();
    setAvailableYears(years);
    setSelectedYear(years.includes("2024") ? "2024" : years[0]);
  }, [salesData]);

  const { chartData } = useMemo(() => {
    // Initialize per-region aggregates
    const regionTotals = {};
    Object.values(regionNameMapping).forEach((region) => {
      regionTotals[region] = { service: 0, other: 0, total: 0 };
    });

    // Build a quick NIP -> region map from clientsData
    const nipToRegion = {};
    clientsData.forEach((c) => {
      const nip = c.nip?.trim();
      if (!nip) return;
      const norm = c.wojewodztwo?.trim().toUpperCase();
      const mapped = regionNameMapping[norm];
      if (mapped) nipToRegion[nip] = mapped;
    });

    // Aggregate
    salesData
      .filter(
        (row) =>
          row.Rok.toString() === selectedYear &&
          (selectedMonth === "00" || parseInt(row.Miesiac) === parseInt(selectedMonth))
      )
      .forEach((row) => {
        const nip = row.NrKontrahenta?.trim();
        const region = nipToRegion[nip];
        if (!region) return; // skip unknown regions

        const domestic =
          parseValue(row.K_15) +
          parseValue(row.K_17) +
          parseValue(row.K_19) +
          parseValue(row.K_10);
        const eu = parseValue(row.K_21) + parseValue(row.K_11);
        const exportVal = parseValue(row.K_22);
        const total = domestic + eu + exportVal;

        const service = parseValue(row.K_21) + parseValue(row.K_17); // Service definition
        const other = Math.max(0, total - service); // everything else

        regionTotals[region].service += service;
        regionTotals[region].other += other;
        regionTotals[region].total += service + other;
      });

    const labels = Object.keys(regionTotals);
    const serviceValues = labels.map((r) => regionTotals[r].service);
    const otherValues = labels.map((r) => regionTotals[r].other);

    // Uniform colors (no red max highlight)
    const serviceBG = labels.map(() => GREEN);
    const serviceBorder = labels.map(() => GREEN_BORDER);
    const otherBG = labels.map(() => ORANGE);
    const otherBorder = labels.map(() => ORANGE_BORDER);

    return {
      chartData: {
        labels,
        datasets: [
          {
            label: "Service",
            data: serviceValues,
            backgroundColor: serviceBG,
            borderColor: serviceBorder,
            borderWidth: 1,
            borderRadius: 4,
          },
          {
            label: "Other",
            data: otherValues,
            backgroundColor: otherBG,
            borderColor: otherBorder,
            borderWidth: 1,
            borderRadius: 4,
          },
        ],
      },
    };
  }, [salesData, clientsData, selectedYear, selectedMonth]);

  return (
    <div style={{ width: "100%", margin: "20px auto", textAlign: "center" }}>
      <ChartTitle>
        Podział transakcji: Service vs Other wg województw
        <InfoIconWrapper data-tooltip-id="types-region-tooltip">
          <Info />
        </InfoIconWrapper>
      </ChartTitle>

      <InfoTooltip
        id="types-region-tooltip"
        place="right"
        style={{ fontSize: "12px", fontFamily: "Arial, sans-serif", color: "#fff" }}
        content={
          <>
            <strong>Service vs Other</strong><br/>
            Wykres pokazuje wartości „Service” (zielony) i „Other” (pomarańczowy) w województwach.
          </>
        }
      />

      <div style={{
        marginBottom: "20px",
        display: "flex",
        justifyContent: "center",
        gap: "12px",
        alignItems: "center"
      }}>
        <label htmlFor="year" style={{ fontWeight: 500 }}>Rok:</label>
        <select
          id="year"
          value={selectedYear}
          onChange={(e) => setSelectedYear(e.target.value)}
          style={{
            padding: "8px 12px",
            fontSize: "15px",
            border: "1px solid #ccc",
            borderRadius: "4px",
            cursor: "pointer"
          }}
        >
          {availableYears.map((year) => (
            <option key={year} value={year}>{year}</option>
          ))}
        </select>

        <label htmlFor="month" style={{ fontWeight: 500 }}>Miesiąc:</label>
        <select
          id="month"
          value={selectedMonth}
          onChange={(e) => setSelectedMonth(e.target.value)}
          style={{
            padding: "8px 12px",
            fontSize: "15px",
            border: "1px solid #ccc",
            borderRadius: "4px",
            cursor: "pointer"
          }}
        >
          <option value="00">Cały rok</option>
          {monthsList.map((m, idx) => (
            <option key={idx} value={idx + 1}>{m}</option>
          ))}
        </select>
      </div>

      <div style={{ width: "100%", maxWidth: "1100px", height: "600px", margin: "0 auto" }}>
        <Bar
          data={chartData}
          options={{
            responsive: true,
            maintainAspectRatio: false,
            plugins: {
              legend: {
                position: "bottom",
                labels: {
                  // Force legend chips to canonical colors (green & orange)
                  generateLabels: (chart) => {
                    const labels = ["Service", "Other"];
                    return labels.map((label, idx) => ({
                      text: label,
                      fillStyle: idx === 0 ? GREEN : ORANGE,
                      strokeStyle: idx === 0 ? GREEN_BORDER : ORANGE_BORDER,
                      lineWidth: 1,
                      hidden: !chart.isDatasetVisible(idx),
                      datasetIndex: idx,
                    }));
                  },
                },
              },
              tooltip: {
                backgroundColor: "#333",
                titleColor: "#fff",
                bodyColor: "#fff",
                callbacks: {
                  title: (items) => items?.[0]?.label ?? "",
                  label: (context) =>
                    `${context.dataset.label}: ${formatNumber(context.raw)}`,
                },
              },
            },
            scales: {
              x: { stacked: true },
              y: {
                stacked: true,
                title: {
                  display: true,
                  text: "Kwota",
                  color: "#666",
                  font: { size: 12, weight: "bold" }
                },
                ticks: {
                  callback: (value) => formatNumber(value)
                }
              },
            },
          }}
        />
      </div>
    </div>
  );
};

export default TransactionTypesByRegion;
